import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Badge,
  DataTable,
  DisplayInactiveToggle,
  EmptyValue,
  FilterField,
  FilterGrid,
  Form,
  HiddenMatchHint,
  LoadErrorState,
  PageHeader,
  ServiceAreaPill,
  StatusBadge,
} from '@/components/ui';
import type { DataTableColumn } from '@/components/ui';
import FiltersCard from '../../../components/FiltersCard';
import ServerPagination from '../../../components/ServerPagination';
import { SearchableDropdown } from '../../../components/SearchableDropdown';
import { areasService } from '../../../services/areasService';
import {
  RESOURCE_GROUP_ORDER,
  RESOURCE_GROUP_TYPES,
  RESOURCE_TYPE_LABELS,
  ResourceType,
  VendorResource,
  groupResourcesByType,
  vendorService,
} from '../../../services/vendorService';
import { useDocumentTitle } from '../../../hooks/useDocumentTitle';
import { usePersistedListState } from '../../../hooks/usePersistedListState';
import ResourceRatesCell from '../components/ResourceRatesCell';
import { BundleComponentsList, BundleComponentsToggle } from '../components/BundleContents';
import { isAwaitingFirstRate } from '../utils/rateDisplay';

const PAGE_SIZES = [50, 100, 200, 500];

/** sessionStorage page key for this page's remembered list state (V2-024). */
const LIST_KEY = 'resources';

interface ResourceFilterState {
  vendorId: string;
  resourceType: ResourceType | '';
  serviceAreaId: string;
  /** Off (the default) lists active resources only (V2-075, INACT-01). */
  displayInactive: boolean;
}

const DEFAULT_RESOURCE_FILTERS: ResourceFilterState = {
  vendorId: '',
  resourceType: '',
  serviceAreaId: '',
  displayInactive: false,
};

/**
 * The all-vendor Resources catalogue (L1, M1, M27), under Admin > Procurement & Materials.
 *
 * It used to be a tab of Vendor Admin that loaded the first 100 resources and grouped those,
 * so 2,611 of 2,711 were simply missing and every group count was wrong. Paging is now done by
 * the server, sorted by group (`sortBy=group`) so each page arrives already in heading order,
 * and the counts beside each type come from the server's `groupCounts` - true totals for the
 * current filters, not the size of the page.
 */
const ResourcesCataloguePage: React.FC = () => {
  useDocumentTitle('Resources');
  const navigate = useNavigate();

  const [rows, setRows] = useState<VendorResource[]>([]);
  const [groupCounts, setGroupCounts] = useState<Partial<Record<ResourceType, number>>>({});
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = usePersistedListState(LIST_KEY, 'page', 1);
  const [pageSize, setPageSize] = usePersistedListState(LIST_KEY, 'pageSize', 100);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  // These filters apply as they change (no Apply step on this page); they, the search and the
  // page are kept for the sign-in (V2-024).
  const [filters, setFilters] = usePersistedListState<ResourceFilterState>(LIST_KEY, 'filters', DEFAULT_RESOURCE_FILTERS);
  const { vendorId, resourceType, serviceAreaId, displayInactive } = filters;
  const setFilter = <K extends keyof ResourceFilterState>(field: K, value: ResourceFilterState[K]) =>
    setFilters((prev) => ({ ...prev, [field]: value }));
  const [search, setSearch] = usePersistedListState(LIST_KEY, 'search', '');
  const [searchInput, setSearchInput] = useState(search);
  // The same search without the Display Inactive filter, for the hidden-match hint (SRCH-05).
  const [unfilteredMatchCount, setUnfilteredMatchCount] = useState<number | null>(null);

  const [vendors, setVendors] = useState<Array<{ id: number; name: string }>>([]);
  const [serviceAreas, setServiceAreas] = useState<Array<{ id: number; name: string }>>([]);

  useEffect(() => {
    vendorService
      .getAllVendors()
      .then((list) => setVendors(list ?? []))
      .catch(() => setVendors([]));
    areasService
      .getServiceAreas()
      .then((areas) => setServiceAreas((areas ?? []).map((a) => ({ id: a.id, name: a.name }))))
      .catch(() => setServiceAreas([]));
  }, []);

  // Any filter change starts again from page 1 - but not on arrival, which restores the page.
  const filterKey = JSON.stringify([vendorId, resourceType, serviceAreaId, displayInactive, search, pageSize]);
  const lastFilterKey = useRef(filterKey);
  useEffect(() => {
    if (lastFilterKey.current === filterKey) return;
    lastFilterKey.current = filterKey;
    setPage(1);
  }, [filterKey]);

  /** Request filters; Display Inactive off sends isActive=true, on sends no status. */
  const query = useCallback(
    (withStatus: boolean) => ({
      nameSearch: search || undefined,
      vendorId: vendorId ? Number(vendorId) : undefined,
      resourceTypes: resourceType ? [resourceType] : undefined,
      serviceAreaIds: serviceAreaId ? [Number(serviceAreaId)] : undefined,
      isActive: withStatus && !displayInactive ? true : undefined,
    }),
    [search, vendorId, resourceType, serviceAreaId, displayInactive]
  );

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await vendorService.getResourcesPaginated({
        page,
        limit: pageSize,
        sortBy: 'group',
        ...query(true),
      });
      setRows(result.data);
      setGroupCounts(result.groupCounts ?? {});
      setTotalCount(result.pagination.totalCount);
      setTotalPages(result.pagination.totalPages);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load resources');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, query]);

  useEffect(() => {
    load();
  }, [load]);

  // Hidden-match hint (V2-076): the same search without the Display Inactive filter, one row,
  // reading the total.
  useEffect(() => {
    if (!search || displayInactive) {
      setUnfilteredMatchCount(null);
      return;
    }
    let cancelled = false;
    vendorService
      .getResourcesPaginated({ page: 1, limit: 1, ...query(false) })
      .then((res) => {
        if (!cancelled) setUnfilteredMatchCount(res.pagination.totalCount);
      })
      .catch(() => {
        if (!cancelled) setUnfilteredMatchCount(null);
      });
    return () => {
      cancelled = true;
    };
  }, [search, displayInactive, query]);

  const toggleExpanded = (id: number) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const allTypesCount = Object.values(groupCounts).reduce((n, c) => n + (c ?? 0), 0);
  /** Anything differs from the defaults (amber chip, Clear All enabled). */
  const hasActiveFilters = !!(vendorId || resourceType || serviceAreaId || displayInactive || search);
  /**
   * Every filter hiding resources, the default Display Inactive off included (V2-076, FLT-05).
   * The search is not counted: it sits beside the chip in the same bar.
   */
  const appliedFilterCount = [vendorId, resourceType, serviceAreaId, !displayInactive].filter(Boolean).length;

  const runSearch = (value: string) => {
    setSearchInput(value.trim());
    setSearch(value.trim());
  };

  const clearFilters = () => {
    setFilters(DEFAULT_RESOURCE_FILTERS);
    setSearchInput('');
    setSearch('');
  };

  const columns: DataTableColumn<VendorResource>[] = useMemo(
    () => [
      {
        key: 'name',
        header: 'Resource',
        mobile: 'title',
        cell: (r) => (
          <span className="tw-inline-flex tw-flex-col tw-gap-0.5">
            <span>{r.name}</span>
            {r.resourceType === 'BUNDLE' && (
              <BundleComponentsToggle
                resource={r}
                expanded={expanded.has(r.id)}
                onToggle={() => toggleExpanded(r.id)}
              />
            )}
          </span>
        ),
      },
      { key: 'vendor', header: 'Vendor', mobile: 'subtitle', cell: (r) => r.vendor?.name ?? '–' },
      {
        key: 'resourceType',
        header: 'Type',
        cell: (r) => (
          <span>
            {RESOURCE_TYPE_LABELS[r.resourceType]}
            {r.isFixedPrice && (
              <Badge bg="secondary" className="tw-ml-2">
                Fixed
              </Badge>
            )}
          </span>
        ),
      },
      { key: 'unit', header: 'Units of Measure', cell: (r) => r.unit ?? '–' },
      { key: 'vehicleType', header: 'Fuel Class', mobile: 'hidden', cell: (r) => r.vehicleType ?? '–' },
      {
        key: 'serviceAreas',
        header: 'Areas',
        mobile: 'hidden',
        cell: (r) =>
          r.serviceAreas?.length ? (
            <span className="tw-flex tw-flex-wrap tw-gap-1">
              {r.serviceAreas.map((sa) => (
                <ServiceAreaPill key={sa.serviceArea.name} value={sa.serviceArea.name} compact />
              ))}
            </span>
          ) : (
            <EmptyValue />
          ),
      },
      { key: 'rates', header: 'Rates', cell: (r) => <ResourceRatesCell rates={r.rates} /> },
      {
        key: 'isActive',
        header: 'Status',
        mobile: 'trailing',
        cell: (r) => (
        isAwaitingFirstRate(r) ? (
          <StatusBadge status="pending" label="Pending" variant="warning" />
        ) : r.isActive ? (
          <StatusBadge status="active" label="Active" variant="success" />
        ) : (
          <StatusBadge status="inactive" label="Inactive" variant="secondary" />
        )
      ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [expanded]
  );

  const groups = groupResourcesByType(rows);
  const firstRow = totalCount === 0 ? 0 : (page - 1) * pageSize + 1;
  const lastRow = Math.min(page * pageSize, totalCount);

  return (
    <div className="tw-p-3 md:tw-p-4">
      <PageHeader
        title="Resources"
        actions={[
          {
            key: 'vendors',
            label: 'All Vendors',
            icon: 'bi-building',
            intent: 'secondary',
            onClick: () => navigate('/vendors'),
          },
          { key: 'refresh', label: 'Refresh', icon: 'bi-arrow-clockwise', intent: 'neutral', onClick: load },
        ]}
      />

      <FiltersCard
        title="Resource Filters"
        subtitle="Filter by vendor, type and service area"
        showClearButton
        onClear={clearFilters}
        hasActiveFilters={hasActiveFilters}
        appliedFilterCount={appliedFilterCount}
        className="tw-mb-3"
        // Searches after a short pause in typing, as the in-panel box did, or on Enter.
        search={{
          value: searchInput,
          onChange: setSearchInput,
          onSearch: runSearch,
          placeholder: 'Resource name',
          label: 'Search resources',
          debounceMs: 300,
        }}
      >
        <FilterGrid min="md">
          <FilterField span={2}>
            {/* A long list, so a searchable select rather than a native one (L6). */}
            <SearchableDropdown
              label="Vendor"
              options={vendors}
              value={vendorId}
              onChange={(v) => setFilter('vendorId', v)}
              getValue={(v) => v.id}
              formatOption={(v) => v.name}
              placeholder="All Vendors"
              allowClear
            />
          </FilterField>
          <FilterField>
            <Form.Group controlId="resource-type-filter">
              <Form.Label>Type</Form.Label>
              <Form.Select value={resourceType} onChange={(e) => setFilter('resourceType', e.target.value as ResourceType | '')}>
                <option value="">All Types</option>
                {RESOURCE_GROUP_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {RESOURCE_TYPE_LABELS[t]}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </FilterField>
          <FilterField>
            <Form.Group controlId="resource-area-filter">
              <Form.Label>Service Area</Form.Label>
              <Form.Select value={serviceAreaId} onChange={(e) => setFilter('serviceAreaId', e.target.value)}>
                <option value="">All Service Areas</option>
                {serviceAreas.map((sa) => (
                  <option key={sa.id} value={sa.id}>
                    {sa.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </FilterField>
          {/* Resources are simply active or inactive (V2-075): a toggle, not a Status dropdown. */}
          <FilterField>
            <DisplayInactiveToggle
              id="resource-display-inactive"
              checked={displayInactive}
              onChange={(checked) => setFilter('displayInactive', checked)}
            />
          </FilterField>
        </FilterGrid>
      </FiltersCard>

      {/* V2-020: on failure, a plain message and Retry – no "0 resources" and no
          "nothing set up yet" alongside it. */}
      {error ? (
        <LoadErrorState what="resources" onRetry={() => void load()} />
      ) : (
      <>
      {unfilteredMatchCount !== null && !loading && (
        <HiddenMatchHint
          search={search}
          shownCount={totalCount}
          unfilteredCount={unfilteredMatchCount}
          shownNoun="active resources"
          hiddenNoun={['inactive match', 'inactive matches']}
          onShow={() => setFilter('displayInactive', true)}
        />
      )}
      {/* True totals per type for the current filters (the type filter itself aside). */}
      <div className="tw-mb-3 tw-flex tw-flex-wrap tw-items-center tw-gap-x-4 tw-gap-y-1 tw-text-sm tw-text-content-secondary" aria-live="polite">
        <span>
          <strong className="tw-text-content">{totalCount.toLocaleString()}</strong> resource{totalCount === 1 ? '' : 's'}
          {totalCount > 0 && ` – showing ${firstRow.toLocaleString()} to ${lastRow.toLocaleString()}`}
        </span>
        {RESOURCE_GROUP_TYPES.map((t, i) => (
          <span key={t}>
            {RESOURCE_GROUP_ORDER[i]}: {(groupCounts[t] ?? 0).toLocaleString()}
          </span>
        ))}
        {resourceType && allTypesCount !== totalCount && (
          <span className="tw-text-content-muted">({allTypesCount.toLocaleString()} across all types)</span>
        )}
      </div>

      {rows.length === 0 ? (
        <DataTable
          columns={columns}
          data={[]}
          rowKey={(r) => r.id}
          loading={loading}
          framed
          emptyMessage={hasActiveFilters ? 'No resources match these filters.' : 'No resources have been set up yet.'}
        />
      ) : (
        groups.map(({ group, resources }) => {
          const type = RESOURCE_GROUP_TYPES[RESOURCE_GROUP_ORDER.indexOf(group as (typeof RESOURCE_GROUP_ORDER)[number])];
          const total = type ? groupCounts[type] ?? resources.length : resources.length;
          return (
            <div key={group} className="tw-mb-4">
              <h2 className="tw-mb-2 tw-text-sm tw-font-semibold tw-tracking-wide tw-text-content-secondary">
                {group}{' '}
                <span className="tw-font-normal tw-text-content-muted">
                  ({total === resources.length ? total.toLocaleString() : `${resources.length} of ${total.toLocaleString()} on this page`})
                </span>
              </h2>
              <DataTable
                columns={columns}
                data={resources}
                rowKey={(r) => r.id}
                framed
                onRowClick={(r) => navigate(`/vendors/${r.vendorId}?tab=resources`)}
                isExpanded={(r) => r.resourceType === 'BUNDLE' && expanded.has(r.id)}
                renderExpanded={(r) => <BundleComponentsList resource={r} />}
                emptyMessage=""
              />
            </div>
          );
        })
      )}
      </>
      )}

      <ServerPagination
        currentPage={page}
        totalPages={totalPages}
        totalCount={totalCount}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        loading={loading}
        pageSizeOptions={PAGE_SIZES}
        className="tw-mt-2"
      />
    </div>
  );
};

export default ResourcesCataloguePage;
